import { useMemo, useState } from "react";

interface UsePaginationOptions {
	initialPage?: number;
	initialLimit?: number;
	initialSortBy?: string;
	initialSortOrder?: "asc" | "desc";
}

export const usePagination = (options: UsePaginationOptions = {}) => {
	const {
		initialPage = 1,
		initialLimit = 10,
		initialSortBy,
		initialSortOrder = "desc",
	} = options;

	const [page, setPage] = useState(initialPage);
	const [limit, setLimit] = useState(initialLimit);
	const [sortBy, setSortBy] = useState(initialSortBy);
	const [sortOrder, setSortOrder] = useState(initialSortOrder);

	const params = useMemo(
		() => ({
			page,
			limit,
			...(sortBy && { sortBy, sortOrder }),
		}),
		[page, limit, sortBy, sortOrder],
	);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handleLimitChange = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	const handleSortChange = (
		newSortBy: string,
		newSortOrder: "asc" | "desc",
	) => {
		setSortBy(newSortBy);
		setSortOrder(newSortOrder);
		setPage(1); // Reset to first page when sorting changes
	};

	const reset = () => {
		setPage(initialPage);
		setLimit(initialLimit);
		setSortBy(initialSortBy);
		setSortOrder(initialSortOrder);
	};

	return {
		page,
		limit,
		sortBy,
		sortOrder,
		params,
		setPage,
		setLimit,
		handlePageChange,
		handleLimitChange,
		handleSortChange,
		reset,
	};
};
